import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { tokens } from "../../../theme";

interface LogIssueDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LogIssueFormData) => Promise<void>;
  equipmentId: string;
  equipmentName?: string;
}

export interface LogIssueFormData {
  issueType: string;   // API enum: damaged | not_working | missing_component
  description: string;
  severity: string;    // API enum: low | medium | high
  date: string;
}

// Label → API value mapping
const ISSUE_TYPES: { label: string; value: string }[] = [
  { label: "Damaged", value: "damaged" },
  { label: "Not Working", value: "not_working" },
  { label: "Missing Component", value: "missing_component" },
];

const SEVERITIES: { label: string; value: string }[] = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
];

const initialForm: LogIssueFormData = {
  issueType: "damaged",
  description: "",
  severity: "low",
  date: new Date().toISOString().split("T")[0],
};

export default function LogIssueDialog({
  open,
  onClose,
  onSubmit,
  equipmentName,
}: LogIssueDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form, setForm] = useState<LogIssueFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(form);
      setForm(initialForm);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeStyle = {
    background: colors.primary[400],
    color: colors.primary[900],
    border: "none",
  };

  const inactiveStyle = {
    background: colors.primary[800],
    color: theme.palette.text.secondary,
    border: "none",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: `1px solid ${colors.primary[400]}` },
    },
    "& .MuiOutlinedInput-input": { padding: "12px 20px" },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: colors.primary[900],
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 14,
          top: 14,
          border: `0.5px solid ${theme.palette.divider}`,
          borderRadius: "50%",
          width: 28,
          height: 28,
          color: theme.palette.text.secondary,
          zIndex: 10,
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <DialogContent sx={{ p: "28px" }}>
        <Typography variant="h5" fontWeight={600} color={colors.primary[400]} mb={0.5}>
          Log Issue
        </Typography>
        {equipmentName && (
          <Typography variant="body2" color={theme.palette.text.secondary} mb={2.5}>
            {equipmentName}
          </Typography>
        )}

        {/* Issue Type */}
        <Typography variant="caption" color={theme.palette.text.secondary} display="block" fontSize={13} mb={1}>
          Issue Type
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2.5}>
          {ISSUE_TYPES.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setForm((p) => ({ ...p, issueType: value }))}
              style={{
                padding: "10px 16px",
                borderRadius: 20,
                fontSize: 12,
                cursor: "pointer",
                fontWeight: form.issueType === value ? 600 : 400,
                transition: "all 0.15s",
                ...(form.issueType === value ? activeStyle : inactiveStyle),
              }}
            >
              {label}
            </button>
          ))}
        </Box>

        {/* Description */}
        <Typography variant="caption" color={theme.palette.text.secondary} display="block" fontSize={13} mb={1}>
          Description{" "}
          <span style={{ color: theme.palette.text.disabled }}>(optional)</span>
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Describe the issue..."
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          sx={{
            ...fieldSx,
            mb: 2.5,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              borderRadius: "16px",
            },
          }}
        />

        {/* Severity */}
        <Typography variant="caption" color={theme.palette.text.secondary} display="block" fontSize={13} mb={1}>
          Severity
        </Typography>
        <Box display="flex" gap={1} mb={2.5}>
          {SEVERITIES.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setForm((p) => ({ ...p, severity: value }))}
              style={{
                padding: "10px 20px",
                borderRadius: 20,
                fontSize: 12,
                cursor: "pointer",
                fontWeight: form.severity === value ? 600 : 400,
                transition: "all 0.15s",
                ...(form.severity === value ? activeStyle : inactiveStyle),
              }}
            >
              {label}
            </button>
          ))}
        </Box>

        {/* Date */}
        <Typography variant="caption" color={theme.palette.text.secondary} display="block" fontSize={13} mb={1}>
          Issue Date
        </Typography>
        <TextField
          type="date"
          fullWidth
          value={form.date}
          onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
          sx={{ ...fieldSx, mb: 3, maxWidth: 220 }}
        />

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              background: colors.primary[400],
              color: colors.primary[900],
              border: "none",
              borderRadius: "12px",
              padding: "14px 36px",
              fontSize: 15,
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {isSubmitting && <CircularProgress size={16} color="inherit" />}
            {isSubmitting ? "Logging..." : "Log Issue"}
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}